import { useState, useRef } from "react";
import { loadResolver } from "../lib/taxonomy";
import { parseResumeFile } from "../lib/parseFile";
import { PANEL, TEAL, CREAM, CREAM60, CREAM30, BORDER, AMBER, NAVY } from "../utils/constants";
import PulseLoader from "./PulseLoader";

/**
 * Step 1 of the journey: intake.
 *
 * The privacy promise is stated ON the screen where the file is chosen, not
 * buried in a policy. It is also literally true — parsing happens here, in the
 * browser, and the file is never sent anywhere (ADR-016).
 */
export default function Intake({ onParsed }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [drag, setDrag] = useState(false);
  const inputRef = useRef(null);

  const handle = async (file) => {
    if (!file) return;
    setError(null);
    setBusy(true);
    try {
      const resolver = await loadResolver();
      const parsed = await parseResumeFile(file, resolver);
      onParsed(parsed);
    } catch (e) {
      setError(e.message || "Could not read that file");
    } finally {
      setBusy(false);
    }
  };

  if (busy) return <PulseLoader text="Reading your profile — on this device…" />;

  return (
    <div style={{ animation: "fadeUp 0.4s ease" }}>
      <div style={{ marginBottom: 20 }}>
        <h2 style={{ color: CREAM, fontSize: 22, margin: "0 0 6px", fontFamily: "'Space Grotesk', sans-serif" }}>
          Start with your profile
        </h2>
        <p style={{ color: CREAM60, fontSize: 14, margin: 0, lineHeight: 1.6 }}>
          Upload your LinkedIn profile export or a résumé. We'll find your role, your
          transferable skills, and where AI is reshaping the work.
        </p>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); handle(e.dataTransfer.files?.[0]); }}
        onClick={() => inputRef.current?.click()}
        style={{
          background: PANEL, border: `1px dashed ${drag ? TEAL : CREAM30}`,
          borderTop: `2px solid ${TEAL}`, borderRadius: 12, padding: "40px 24px",
          textAlign: "center", cursor: "pointer", transition: "border-color 0.2s",
        }}
      >
        <div style={{ fontSize: 15, color: CREAM, fontWeight: 600, marginBottom: 6 }}>
          Drop your file here, or click to choose
        </div>
        <div style={{ fontSize: 12, color: CREAM30, fontFamily: "'JetBrains Mono', monospace" }}>
          PDF or DOCX
        </div>
        <input
          ref={inputRef} type="file" accept=".pdf,.docx" hidden
          onChange={(e) => handle(e.target.files?.[0])}
        />
      </div>

      {error && (
        <div style={{ background: `${AMBER}10`, border: `1px solid ${AMBER}30`, borderRadius: 8,
                      padding: "8px 14px", marginTop: 14, fontSize: 12, color: AMBER }}>
          {error}
        </div>
      )}

      <div style={{ background: `${TEAL}08`, border: `1px solid ${TEAL}30`, borderRadius: 10,
                    padding: 16, marginTop: 20 }}>
        <div style={{ fontSize: 12, color: TEAL, fontWeight: 600, marginBottom: 8,
                      textTransform: "uppercase", letterSpacing: 1, fontFamily: "'JetBrains Mono', monospace" }}>
          Your file stays on your device
        </div>
        <p style={{ color: CREAM60, fontSize: 13, lineHeight: 1.6, margin: 0 }}>
          We read it here in your browser. The document is never uploaded, never stored,
          and never sent to a server — including ours. Only the role and skills we find
          are used for the analysis.
        </p>
      </div>

      <details style={{ marginTop: 16 }}>
        <summary style={{ color: CREAM30, fontSize: 12, cursor: "pointer",
                          fontFamily: "'JetBrains Mono', monospace" }}>
          How do I export my LinkedIn profile?
        </summary>
        <ol style={{ color: CREAM60, fontSize: 13, lineHeight: 1.7, marginTop: 10, paddingLeft: 20 }}>
          <li>Open your LinkedIn profile</li>
          <li>Click <strong style={{ color: CREAM }}>More</strong> under your headline</li>
          <li>Choose <strong style={{ color: CREAM }}>Save to PDF</strong></li>
        </ol>
      </details>
    </div>
  );
}
